import {
  Component,
  computed,
  DoCheck,
  ElementRef,
  HostListener,
  inject,
  input,
  InputSignal,
  NgZone,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormInputComponent } from '../form-input.component';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { ButtonStyle } from '../../../feedback/button/button.component';
import { ICorporateDesign } from '@web/dtos';

export interface IButtonOption<T> {
  label: string;
  value: T;
  iconSrc?: string;
}

@Component({
  selector: 'aw-button-group',
  templateUrl: './button-group.component.html',
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    AppendTestIdPipe,
    NgStyle,
  ],
})
export class ButtonGroupComponent<T>
  extends FormInputComponent
  implements OnInit, DoCheck
{
  public readonly options: InputSignal<IButtonOption<T>[]> =
    input.required<IButtonOption<T>[]>();
  public readonly style: InputSignal<ButtonStyle> =
    input<ButtonStyle>('primary');
  public readonly buttonWidth: InputSignal<number> = input<number>(196);
  public readonly buttonsPerRow: InputSignal<number> = input<number>(0);

  public readonly compact: InputSignal<boolean> = input(false);
  public readonly ariaLabel: InputSignal<string | undefined> = input<string>();

  private readonly ngZone: NgZone = inject(NgZone);

  public readonly valueChange: OutputEmitterRef<string> = output<string>();
  @ViewChild('buttonGroup')
  protected readonly buttonGroupRef!: ElementRef;
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.styleButtons();
  }
  public hoveredMouseButton: WritableSignal<number | undefined> =
    signal(undefined);

  public corporateBackgroundColor: Signal<string> = computed(() => {
    const cDesign: ICorporateDesign | undefined = this.corporateDesign();
    if (this.siteContextService.isShopContext()) {
      switch (this.style()) {
        case 'primary':
          return this.corporateDesignService.primaryColor();
        case 'secondary':
          return cDesign.secondaryColor;
        case 'error':
        case 'error-alt':
          return '#CE4545';
        default:
          return '';
      }
    }
    return '';
  });

  public corporateBorderColor: Signal<string> = computed(() => {
    const cDesign: ICorporateDesign | undefined = this.corporateDesign();
    if (this.siteContextService.isShopContext()) {
      switch (this.style()) {
        case 'primary':
          return cDesign.primaryBorderColor;
        case 'primary-alt':
          return this.corporateDesignService.linkIconColor();
        case 'secondary':
          return cDesign.secondaryFontColor;
        case 'secondary-alt':
          return cDesign.secondaryColor;
        case 'error':
        case 'error-alt':
          return '#CE4545';
        default:
          return '';
      }
    }
    return '';
  });
  public corporateFontColor: Signal<string> = computed(() => {
    const cDesign: ICorporateDesign | undefined = this.corporateDesign();
    if (this.siteContextService.isShopContext()) {
      switch (this.style()) {
        case 'primary':
          return this.corporateDesignService.primaryFontColor();
        case 'primary-alt':
          return this.corporateDesignService.linkIconColor();
        case 'secondary':
          return cDesign.secondaryFontColor;
        case 'secondary-alt':
          return cDesign.secondaryColor;
        case 'error':
        case 'error-alt':
          return '#FFF';
        default:
          return '';
      }
    }
    return '';
  });

  public hoverBackgroundColor: Signal<string> = computed(() => {
    const cDesign: ICorporateDesign | undefined = this.corporateDesign();
    const opacity = '33';
    if (this.siteContextService.isShopContext()) {
      switch (this.style()) {
        case 'primary':
          return this.corporateDesignService.primaryColor() + opacity;
        case 'primary-alt':
          return this.corporateDesignService.linkIconColor() + opacity;
        case 'secondary':
          return cDesign.secondaryColor + opacity;
        case 'secondary-alt':
          return cDesign.secondaryColor + opacity;
        case 'error':
        case 'error-alt':
          return '#672222';
        default:
          return '';
      }
    }
    return '';
  });
  public hoverBorderColor: Signal<string> = computed(() => {
    const cDesign: ICorporateDesign | undefined = this.corporateDesign();
    if (this.siteContextService.isShopContext()) {
      switch (this.style()) {
        case 'primary':
          return cDesign.primaryBorderColor;
        case 'primary-alt':
          return this.corporateDesignService.linkIconColor();
        case 'secondary':
          return cDesign.secondaryFontColor;
        case 'secondary-alt':
          return cDesign.secondaryColor;
        case 'error':
        case 'error-alt':
          return '#672222';
        default:
          return '';
      }
    }
    return '';
  });
  public hoverFontColor: Signal<string> = computed(() => {
    const cDesign: ICorporateDesign | undefined = this.corporateDesign();
    if (this.siteContextService.isShopContext()) {
      switch (this.style()) {
        case 'primary':
          return this.corporateDesignService.primaryFontColor();
        case 'primary-alt':
          return this.corporateDesignService.linkIconColor();
        case 'secondary':
          return cDesign.secondaryColor;
        case 'secondary-alt':
          return cDesign.secondaryColor;
        case 'error':
        case 'error-alt':
          return '#FFF';
        default:
          return '';
      }
    }
    return '';
  });

  ngOnInit() {
    this.subscription.add(
      this.ngControl.control!.valueChanges.subscribe(value => {
        this.ngControl.control!.markAsTouched();
        this.valueChange.emit(value);
      })
    );
  }

  ngDoCheck() {
    if (this.compact()) {
      return;
    }
    this.ngZone.runOutsideAngular(() =>
      setTimeout(() => this.styleButtons(), 0)
    );
  }

  changeActive(option: IButtonOption<T>) {
    this.ngControl.control!.setValue(option.value, { emitEvent: false });
    this.ngControl.control!.updateValueAndValidity();
  }

  private styleButtons(): void {
    if (
      !this.compact() &&
      this.buttonGroupRef?.nativeElement?.children?.length > 0
    ) {
      const buttonGroupWidth =
        this.buttonGroupRef.nativeElement.getBoundingClientRect().width;
      const percentButtonWidth: number = Math.floor(
        buttonGroupWidth / (this.buttonsPerRow() > 0 ? this.buttonsPerRow() : 1)
      );
      const buttons: HTMLElement[] = Array.from(
        this.buttonGroupRef.nativeElement.children
      );
      const buttonWidth: number =
        this.buttonsPerRow() <= 0 ? this.buttonWidth() : percentButtonWidth; //buttons[0].getBoundingClientRect().width;
      const buttonsPerRow: number =
        this.buttonsPerRow() <= 0
          ? Math.floor(buttonGroupWidth / buttonWidth)
          : this.buttonsPerRow();
      const lastRowIndex: number = buttons.length - buttonsPerRow;
      const isInFirstRow: (index: number) => boolean = (
        index: number
      ): boolean => index < buttonsPerRow;
      const isInFirstColumn: (index: number) => boolean = (
        index: number
      ): boolean => index % buttonsPerRow === 0;

      buttons.forEach((button: HTMLElement, index: number) => {
        button.style.borderTopLeftRadius = '';
        button.style.borderTopRightRadius = '';
        button.style.borderTopWidth = '';
        button.style.borderLeftWidth = '';
        button.style.borderBottomRightRadius = '';
        button.style.borderBottomLeftRadius = '';
        button.style.width = buttonWidth + 'px';

        if (index !== 0) {
          button.style.borderTopLeftRadius = '0px';
        }
        if (
          index !== buttonsPerRow - 1 &&
          !(index === buttons.length - 1 && buttons.length <= buttonsPerRow)
        ) {
          button.style.borderTopRightRadius = '0px';
        }
        if (isInFirstRow(index)) {
          button.style.borderTopWidth = '1px';
        }
        if (isInFirstColumn(index)) {
          button.style.borderLeftWidth = '1px';
        }
        if (
          (index % buttonsPerRow !== buttonsPerRow - 1 &&
            index !== buttons.length - 1) ||
          index < lastRowIndex
        ) {
          button.style.borderBottomRightRadius = '0px';
        }
        if (
          index < lastRowIndex ||
          (index >= lastRowIndex && index % buttonsPerRow !== 0)
        ) {
          button.style.borderBottomLeftRadius = '0px';
        }
      });
    }
  }
}
